import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import { capacitorUpdaterService } from 'src/utils/Services/capacitor-updater.service';

import { CONFIG } from 'src/environments/environment';

/**
 * Enhanced function to compare two version strings with comprehensive edge case handling.
 * Supports semantic versioning (x.y.z) with optional pre-release and build metadata.
 * @param currentVersion - The current app version.
 * @param minimumVersion - The minimum required version.
 * @returns {boolean} - True if the current version is greater than or equal to the minimum version.
 */
export const isAppVersionValid = (currentVersion: string, minimumVersion: string): boolean => {
  // Input validation
  if (!currentVersion || !minimumVersion) {
    console.warn('Version comparison: Invalid input', { currentVersion, minimumVersion });
    return false;
  }

  if (typeof currentVersion !== 'string' || typeof minimumVersion !== 'string') {
    console.warn('Version comparison: Inputs must be strings', { currentVersion, minimumVersion });
    return false;
  }

  // Normalize versions by removing pre-release/build metadata and ensuring 3 parts
  const normalizeVersion = (version: string): string => {
    const trimmed = version.trim();
    // Remove pre-release and build metadata (everything after - or +)
    const coreVersion = trimmed.split(/[-+]/)[0];
    const parts = coreVersion.split('.');

    // Ensure we have exactly 3 parts (major.minor.patch)
    while (parts.length < 3) {
      parts.push('0');
    }

    // Take only the first 3 parts and ensure they're valid numbers
    const normalizedParts = parts.slice(0, 3).map((part) => {
      const num = parseInt(part, 10);
      return Number.isNaN(num) ? 0 : num;
    });

    return normalizedParts.join('.');
  };

  // Validate version format (basic check)
  const versionRegex = /^(\d+)\.(\d+)(?:\.(\d+))?(?:[-+].*)?$/;
  if (!versionRegex.test(currentVersion.trim()) || !versionRegex.test(minimumVersion.trim())) {
    console.warn('Version comparison: Invalid version format', { currentVersion, minimumVersion });
    return false;
  }

  // Normalize and parse versions
  const normalizedCurrent = normalizeVersion(currentVersion);
  const normalizedMinimum = normalizeVersion(minimumVersion);

  const currentParts = normalizedCurrent.split('.').map((part) => parseInt(part, 10));
  const minimumParts = normalizedMinimum.split('.').map((part) => parseInt(part, 10));

  // Compare each part (major, minor, patch)
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < Math.max(currentParts.length, minimumParts.length); i++) {
    const current = currentParts[i] || 0;
    const minimum = minimumParts[i] || 0;

    // Safety check for NaN (shouldn't happen after normalization)
    if (Number.isNaN(current) || Number.isNaN(minimum)) {
      console.warn('Version comparison: NaN detected', { current, minimum, index: i });
      return false;
    }

    if (current > minimum) {
      return true;
    }
    if (current < minimum) {
      return false;
    }
  }
  return true; // Versions are equal
};

export const getAppVersion = async (): Promise<string | null> => {
  try {
    // For native platforms, get the current bundle version from Capacitor Updater
    // This will return the OTA update version if one is installed
    if (Capacitor.isNativePlatform()) {
      try {
        const currentBundle = await capacitorUpdaterService.getCurrentVersion();
        const bundleVersion = currentBundle?.bundle?.version;
        const bundleId = currentBundle?.bundle?.id;

        // Check if bundle version or ID is "builtin" - this means we're running the native build
        // In this case, we should use the actual native version from App.getInfo()
        if (bundleVersion === 'builtin' || bundleId === 'builtin') {
          console.log('Detected builtin bundle, using native app version');
          const info = await App.getInfo();
          return info.version || null;
        }

        // Return the bundle version if available and valid (this is the running OTA version)
        if (bundleVersion && bundleVersion !== 'builtin') {
          return bundleVersion;
        }

        // Fallback to native app version if no OTA update is installed
        const info = await App.getInfo();
        return info.version || null;
      } catch (updaterError) {
        // If capacitor updater fails, fallback to native app version
        console.warn('Capacitor Updater failed, falling back to native version:', updaterError);
        const info = await App.getInfo();
        return info.version || null;
      }
    }

    // For web platform, try to get app info first
    const info = await App.getInfo();
    return info.version || CONFIG.DEFAULT_APP_VERSION_BECAUSE_OF_WEB;
  } catch (error) {
    // Fallback for web platform or any other errors
    return CONFIG.DEFAULT_APP_VERSION_BECAUSE_OF_WEB;
  }
};

/**
 * Get the display version for the app based on platform
 * @returns The version string to display ("Latest" for web, actual version for native)
 */
export const getDisplayAppVersion = (currentVersion: string | undefined, isNativePlatform: boolean): string => {
  // For web platform, always show "Latest"
  if (!isNativePlatform) {
    return 'Latest';
  }

  // For native platforms, show the actual version or fallback to '0.0.0'
  return currentVersion || '0.0.0';
};
